import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CartController } from "../controllers/cart";
import { track } from "../lib/analytics";
import { useAuth } from "./AuthContext";
import { rememberCartMeta } from "../lib/cartMeta";
import { availableForSku } from "../lib/stockGuard";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Toast bus: holds the last item added so the global <AddToCartToast/>
  // mounted in App can render and dismiss it.
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    CartController.get()
      .then((c) => { if (!cancelled) setItems(c?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const add = useCallback(async (sku, qty = 1, meta = null) => {
    // Cap by total available stock so add-to-cart can never push the
    // cart over what we can ship. Existing line qty counts toward the
    // cap.
    const stock = availableForSku(sku);
    const existing = (items || []).find((i) => i.sku === sku)?.qty || 0;
    let appliedQty = qty;
    let capped = false;
    if (typeof stock === "number") {
      const headroom = Math.max(0, stock - existing);
      if (headroom <= 0) {
        appliedQty = 0;
        capped = true;
      } else if (qty > headroom) {
        appliedQty = headroom;
        capped = true;
      }
    }

    if (appliedQty > 0) {
      const next = await CartController.addItem(items, sku, appliedQty);
      setItems(next?.items || []);
      track("add_to_cart", { sku, qty: appliedQty });
    }

    const toastBase = { sku, qty: appliedQty, capped, stock, _t: Date.now() };
    if (meta) {
      rememberCartMeta(sku, meta);
      setToast({ ...meta, ...toastBase });
    } else {
      setToast(toastBase);
    }
  }, [items]);

  const remove = useCallback(async (sku) => {
    const next = await CartController.removeItem(items, sku);
    setItems(next?.items || []);
    track("remove_from_cart", { sku });
  }, [items]);

  const updateQty = useCallback(async (sku, qty) => {
    if (qty <= 0) return remove(sku);
    const next = (items || []).map((i) => i.sku === sku ? { ...i, qty } : i);
    setItems(next);
    await CartController.update(next).catch(() => null);
  }, [items, remove]);

  const dismissToast = useCallback(() => setToast(null), []);

  const count = useMemo(() => items.reduce((n, i) => n + (i.qty || 0), 0), [items]);

  return (
    <CartContext.Provider value={{ items, count, loading, add, remove, updateQty, setItems, toast, dismissToast }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
